/**
 * The settings panels, as data — the same treatment the module sections get.
 *
 * S-D3 drew eight. **All eight are built as of S-G1, and there are nine as of
 * SF-32** — the ninth is *Audit log*, which S-D3 did not draw and SUP-41 requires;
 * its own entry below says why it is here rather than folded into another panel.
 * The history of the four that were not built at first is kept because it is the
 * record of what each one was waiting for — a panel listed here disabled was
 * always waiting on a backend, never on somebody getting round to it:
 *
 *  * ~~**Connections**~~ — **built in S-B17**, once there was something to
 *    connect to: GSC (S-B22), LinkedIn (S-B17), sender identity (S-B18).
 *  * ~~**Brand voice**~~ — **built in S-G1.** The wizard has written this row
 *    since S-B9 and `app/seo/content.py` has read it since S-B25; what was
 *    missing was a way to change it once the wizard was behind you, which
 *    S-D2's own step-6 copy promises. It edits the same singleton row over the
 *    wizard's own endpoints rather than a second pair of routes.
 *  * ~~**Budget caps**~~ — **built in S-B7.** The ledger it needed exists: a
 *    ceiling per tenant, checked before every paid call, with the one-off raise
 *    `admin#overage` offers. Visible to every member and editable by owners and
 *    admins, which is what S-D3's role matrix draws.
 *  * ~~**Notifications**~~ — **built in S-B10.** The concern S-B8 recorded here
 *    was that a preferences table keyed on events no code emits would be a
 *    schema written against a guess; the answer was to take the vocabulary from
 *    S-D3's own table — five approved rows — and record, per event, which
 *    session emits it. Three are emitted today and the rest render with a
 *    "from S-Bxx" note, the same treatment the sidebar gives an unshipped
 *    module. The panel also carries the webhook endpoints and their delivery
 *    log, because S-D3 designed no panel for those and adding a ninth entry to
 *    approved navigation would be inventing information architecture.
 *  * ~~**Danger zone**~~ — **built in S-B12.** DPDP export and organisation
 *    deletion are §8 launch requirements that session owns, and both are live.
 *    Pause-all-automation is still there and still disabled, because there is
 *    genuinely no automation to pause until the SEO and outreach runners land —
 *    rendered rather than removed, so the approved panel keeps its shape.
 *
 * The convention, kept for whatever S-D3 draws next: a panel with no backend is
 * shown disabled rather than hidden, exactly as the sidebar sections are, so the
 * settings information architecture is visible and none of it is a stub. What
 * S-G1 found is the failure mode that convention has — `arrivesIn` naming a
 * session that closed weeks earlier, with nobody looking at it again — so
 * `panels.test.ts` now asserts that every disabled entry names a session that is
 * genuinely still open.
 */

/**
 * Settings consolidation (2026-09-15). The nav is drawn under three headings:
 * the organisation-level panels S-D3 approved, then the outreach and SEO
 * settings that used to be edited on working pages. A group with no visible
 * panel is not drawn.
 */
export const SETTINGS_GROUPS = ['Organisation', 'Outreach', 'SEO'] as const
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]

export interface SettingsPanel {
  label: string
  group: SettingsGroup
  to?: string
  /** Permission required to *see* it — mirrors the route dependency on the API. */
  permission?: string
  /**
   * The plan session that brings — or brought — the panel. Absent only for a
   * panel built outside the numbered plan: *Branding* and *Domain* came from
   * the tenant-hosts spec (`docs/superpowers/specs/2026-09-13-tenant-hosts-
   * and-white-label.md`), which has no row in `IMPLEMENTATION-PLAN.md`. A
   * disabled entry must still name one, because that is what `panels.test.ts`
   * reads to notice a "soon" that has shipped.
   */
  arrivesIn?: string
}

export const SETTINGS_PANELS: readonly SettingsPanel[] = [
  {
    label: 'Organisation',
    group: 'Organisation',
    to: '/settings/organisation',
    permission: 'tenant:read',
    arrivesIn: 'S-B8',
  },
  {
    label: 'Team & roles',
    group: 'Organisation',
    to: '/settings/team',
    permission: 'members:read',
    arrivesIn: 'S-B8',
  },
  {
    // S-B17. **Back to S-D3's eight.** S-B14 added a ninth entry — *Browser
    // extension* — because the panel it belonged in did not exist and an
    // extension nobody could pair was an extension nobody could use. That panel
    // exists now, the extension is a card inside it, and the ninth entry is gone
    // exactly as S-B14 said it would be. `/settings/extension` redirects here.
    //
    // `tenant:read` rather than `discovery:manage`: a member who cannot connect
    // LinkedIn still needs to see whether it is connected, because that is why
    // their outreach is or is not going out. The card's own controls are
    // refused by the API, which is the boundary that actually holds.
    label: 'Connections',
    group: 'Organisation',
    to: '/settings/connections',
    permission: 'tenant:read',
    arrivesIn: 'S-B17',
  },
  {
    // S-G1. **Was the drift.** This read `{ label: 'Brand voice', arrivesIn:
    // 'S-B25' }` — disabled, naming a session that closed on 2026-08-11 — long
    // after the row it edits was being written by the wizard and read by
    // content generation. `tenant:read` for the same reason Connections uses
    // it: every role needs to see the voice their drafts are written in, and
    // the API refuses a member's write regardless.
    label: 'Brand voice',
    group: 'Organisation',
    to: '/settings/brand-voice',
    permission: 'tenant:read',
    arrivesIn: 'S-B25',
  },
  {
    // Spec 2026-09-13, *Branding*. The logo the organisation's own host wears,
    // and its workspace address. `tenant:update` rather than `tenant:read`
    // because, unlike Connections and Brand voice, a member has nothing to
    // *see* here they do not already see in the rail: the panel is the
    // controls. The API gates the writes on the same permission.
    label: 'Branding',
    group: 'Organisation',
    to: '/settings/branding',
    permission: 'tenant:update',
  },
  {
    // Spec 2026-09-13, *Custom domains*. Same gate, same reason.
    label: 'Domain',
    group: 'Organisation',
    to: '/settings/domain',
    permission: 'tenant:update',
  },
  {
    label: 'Budget caps',
    group: 'Organisation',
    to: '/settings/budget',
    permission: 'tenant:read',
    arrivesIn: 'S-B7',
  },
  {
    label: 'Notifications',
    group: 'Organisation',
    to: '/settings/notifications',
    permission: 'tenant:read',
    arrivesIn: 'S-B10',
  },
  { label: 'Active sessions', group: 'Organisation', to: '/settings/sessions', arrivesIn: 'S-B8' },
  {
    // SUP-41, SF-32. **The ninth entry, and S-D3 drew eight.** Added rather than
    // folded into an existing panel because it is not a setting: nothing on it
    // can be changed, which is the property it exists to demonstrate. It sits
    // after *Active sessions* and before *Danger zone* because the three are one
    // group — who is in this account, what has been done in it, and how to leave.
    //
    // `audit:read` is the API's own dependency on `GET /api/v1/audit`
    // (`_MAY_READ_AUDIT`), mirrored here so a member who cannot read the log is
    // not offered a panel that will refuse them.
    label: 'Audit log',
    group: 'Organisation',
    to: '/settings/audit',
    permission: 'audit:read',
    arrivesIn: 'S-B12',
  },
  {
    label: 'Danger zone',
    group: 'Organisation',
    to: '/settings/danger-zone',
    permission: 'data:export',
    arrivesIn: 'S-B12',
  },
  // --- Outreach ------------------------------------------------------------
  // Settings consolidation (2026-09-15). `tenant:read` to see the panel; the
  // panel hides Edit without `budget:manage`, which is what
  // `PUT /automation/send-window` is gated on.
  {
    label: 'Send window',
    group: 'Outreach',
    to: '/settings/send-window',
    permission: 'tenant:read',
  },
  // Gated on `discovery:manage` by `PUT /outreach/discovery-settings`; the
  // panel hides Edit without it.
  {
    label: 'Discovery rules',
    group: 'Outreach',
    to: '/settings/discovery-rules',
    permission: 'tenant:read',
  },
  // Batch 2 (2026-09-15). Gated on `outreach:manage` by the ladder and cap
  // writes; the panel hides Edit without it.
  {
    label: 'Follow-ups',
    group: 'Outreach',
    to: '/settings/follow-ups',
    permission: 'tenant:read',
  },
  // --- SEO -----------------------------------------------------------------
  // Per site: each panel carries a site chip (`settings/site.ts`). The three
  // writes are gated on `tenant:update`; the panels hide Edit without it.
  {
    label: 'Run schedule',
    group: 'SEO',
    to: '/settings/run-schedule',
    permission: 'tenant:read',
  },
  {
    label: 'Approvals',
    group: 'SEO',
    to: '/settings/approvals',
    permission: 'tenant:read',
  },
  {
    label: 'Publish target',
    group: 'SEO',
    to: '/settings/publish-target',
    permission: 'tenant:read',
  },
  // Batch 2 (2026-09-15). Uptime is per site and its write is `discovery:manage`
  // (`app/v1/uptime.py`); the quality gate is organisation-wide on `tenant:update`.
  {
    label: 'Uptime',
    group: 'SEO',
    to: '/settings/uptime',
    permission: 'tenant:read',
  },
  {
    label: 'Content quality',
    group: 'SEO',
    to: '/settings/content-quality',
    permission: 'tenant:read',
  },
]

/* `panelForPath` lived here until SF-18. Its only caller was `AppShell`'s
   breadcrumb trail, which SHELL-19 cut from four crumbs to two — the panel's
   name is the `h1` of the screen you are looking at and the selected entry in
   the sub-nav beside it, so the trail was restating the page twice. Removed
   rather than left exported: a lookup nothing calls is one the next session
   reads as the supported way to do something. */
